// FLAGS: none
// Reads and edits which session-type/language pairs an expert is closed for — booking availability, not client data.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";
import { Experts } from "@src/api/v1/Experts";
import { RequestExpertsSessionTypeLanguagePairRequest } from "@src/api/v1/data-contracts";

const closedPairsKey = (expertId: string) =>
  ["experts", expertId, "language-availability"] as const;

const exclusionsKey = [
  "experts",
  "language-availability",
  "exclusions",
] as const;

export const useExpertsWithLanguageAvailabilityExclusionsQuery = (
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: exclusionsKey,
    queryFn: () =>
      new Experts(
        OpenUpHttpClient,
      ).getExpertIdsWithLanguageAvailabilityExclusions(),
    enabled,
    suspense: false,
  });

export const useExpertLanguageAvailabilityQuery = (
  expertId: string,
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: closedPairsKey(expertId),
    queryFn: () =>
      new Experts(OpenUpHttpClient).getLanguageAvailability(expertId),
    staleTime: 0,
    refetchOnMount: "always",
    enabled: enabled && !!expertId,
  });

export const useUpdateExpertLanguageAvailability = (expertId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (closedPairs: RequestExpertsSessionTypeLanguagePairRequest[]) =>
      new Experts(OpenUpHttpClient).updateLanguageAvailability(
        expertId,
        closedPairs,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: closedPairsKey(expertId) });
      queryClient.invalidateQueries({ queryKey: exclusionsKey });
    },
  });
};
